import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
// Index is the homepage / most-visited route — keep it eager so first paint
// isn't gated behind an extra chunk fetch. Everything else is lazy so it stays
// out of the primary bundle.
import Index from "./pages/Index.tsx";
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy.tsx"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions.tsx"));
const ModuleRoute = lazy(() => import("./pages/module/ModuleRoute.tsx"));
const BlogList = lazy(() => import("./pages/blog/BlogList.tsx"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost.tsx"));
import { LangLayout } from "@/i18n/routing";
import { RootLanguageRedirect } from "@/i18n/RootRedirect";
import { NON_DEFAULT_LANGS, type Lang } from "@/i18n/types";

const queryClient = new QueryClient();

function ScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      // Strip query/fragment garbage so URLs like "#aicalls?utm_source=..."
      // don't blow up querySelector with an invalid selector.
      const id = hash.slice(1).split(/[?&]/)[0];
      if (id && /^[A-Za-z][\w-]*$/.test(id)) {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
          return;
        }
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [pathname, hash]);

  return null;
}

// The same set of pages, rendered under each language's layout.
const pageRoutes = () => (
  <>
    <Route index element={<Index />} />
    <Route path="privacy" element={<PrivacyPolicy />} />
    <Route path="cookies" element={<CookiePolicy />} />
    <Route path="terms" element={<TermsAndConditions />} />
    <Route path="module/:slug" element={<ModuleRoute />} />
    <Route path="blog" element={<BlogList />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="*" element={<NotFound />} />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToHash />
        <RootLanguageRedirect />
        <Suspense fallback={null}>
          <Routes>
            {/* Default language (Romanian) lives at the root, no prefix. */}
            <Route path="/" element={<LangLayout lang="ro" />}>
              {pageRoutes()}
            </Route>

            {/* Every other language is served under its prefix (/en, /es, ...). */}
            {NON_DEFAULT_LANGS.map((lang: Lang) => (
              <Route key={lang} path={`/${lang}`} element={<LangLayout lang={lang} />}>
                {pageRoutes()}
              </Route>
            ))}
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
